import { useCallback, useEffect, useState } from 'react';
import {
  Boxes, Loader2, Plus, RefreshCw, Search, X, AlertTriangle, Network,
  ArrowUp, ArrowDown, Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  cmdbApi, queueName, type CiImpact, type CmdbMeta, type ConfigItem, type ItsmItem,
} from '../services/api';

/**
 * The CMDB — owned assets, their owners and what depends on them (PO51-1174).
 *
 * **A CI is an asset, not work.** It has no delivery lifecycle and never flows
 * backlog → done, which is why this is a table rather than a board: a board
 * implies a pipeline, and a database server is not moving through one.
 *
 * The **impact view** is the reason this exists rather than a spreadsheet.
 * "What breaks if this goes" is a transitive question, and it is the one asked
 * during an outage — so it is one click from the asset, with the owner groups
 * to notify already resolved.
 */

const LIFECYCLE_STYLE: Record<string, string> = {
  planned: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300',
  active: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
  maintenance: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300',
  degraded: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
  retired: 'bg-gray-100 dark:bg-gray-700 text-gray-400',
};

export default function CmdbPage() {
  const [items, setItems] = useState<ConfigItem[]>([]);
  const [meta, setMeta] = useState<CmdbMeta | null>(null);
  const [q, setQ] = useState('');
  const [ciType, setCiType] = useState('');
  const [ownerGroup, setOwnerGroup] = useState('');
  const [includeRetired, setIncludeRetired] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<ConfigItem | 'new' | null>(null);
  const [inspecting, setInspecting] = useState<ConfigItem | null>(null);

  useEffect(() => { cmdbApi.meta().then(setMeta).catch(() => setMeta(null)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await cmdbApi.list({
        q: q || undefined, ciType: ciType || undefined,
        ownerGroup: ownerGroup || undefined, includeRetired,
      });
      setItems(data.items || []);
    } catch (err: any) {
      toast.error(err.message || 'Could not load the CMDB');
    } finally {
      setLoading(false);
    }
  }, [q, ciType, ownerGroup, includeRetired]);

  useEffect(() => { load(); }, [load]);

  const remove = async (ci: ConfigItem) => {
    if (!confirm(`Delete "${ci.name}"? Retiring it is usually what you want — `
      + `the tickets that reference it stay readable.`)) return;
    try {
      await cmdbApi.remove(ci._id);
      toast.success('Deleted');
      load();
    } catch (err: any) {
      // The server names what depends on it, which is the useful part.
      toast.error(err.message || 'Could not delete', { duration: 9000 });
    }
  };

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Boxes size={20} className="text-accent" />
            CMDB
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            The owned assets operations hangs off. Every ticket is anchored to
            one of these, and its owner group is who the work routes to.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={() => setEditing('new')}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-accent text-white">
            <Plus size={14} /> New asset
          </button>
          <button onClick={load}
            className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent hover:text-accent">
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Name, description, environment…"
            className="pl-8 pr-3 py-1.5 w-64 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
        </div>
        <select value={ciType} onChange={(e) => setCiType(e.target.value)}
          className="px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
          <option value="">All types</option>
          {(meta?.ciTypes || []).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={ownerGroup} onChange={(e) => setOwnerGroup(e.target.value)}
          aria-label="Filter by owner queue"
          className="px-2 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
          <option value="">All owners</option>
          {(meta?.ownerGroups || []).map((o) => <option key={o} value={o}>{queueName(o, meta?.queues)}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <input type="checkbox" checked={includeRetired}
            onChange={(e) => setIncludeRetired(e.target.checked)} />
          Show retired
        </label>
        <span className="text-xs text-gray-400 ml-auto">{items.length}</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={22} className="animate-spin text-accent" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <Boxes size={44} className="text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">No assets yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
            Until something is here, a ticket has nothing to be anchored to and
            no owner to route to. Add the systems, services and infrastructure
            you actually run.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400">
              <tr>
                <th className="text-left font-medium px-3 py-2">Asset</th>
                <th className="text-left font-medium px-3 py-2">Type</th>
                <th className="text-left font-medium px-3 py-2">Owner</th>
                <th className="text-left font-medium px-3 py-2">State</th>
                <th className="text-left font-medium px-3 py-2">Depends on</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {items.map((ci) => (
                <tr key={ci._id}
                  className="border-t border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-3 py-2">
                    <button onClick={() => setEditing(ci)}
                      className="text-left text-gray-900 dark:text-gray-100 hover:text-accent">
                      {ci.name}
                    </button>
                    {!ci.ownerGroup && (
                      /* An unowned asset cannot route a ticket — worth seeing
                         in the list, not just at creation. */
                      <span className="ml-2 inline-flex items-center gap-1 text-[10px] text-amber-600 dark:text-amber-500">
                        <AlertTriangle size={10} /> no owner
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">{ci.ciType}</td>
                  <td className="px-3 py-2 text-gray-500 dark:text-gray-400">
                    {queueName(ci.ownerGroup, meta?.queues) || '—'}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${LIFECYCLE_STYLE[ci.lifecycleState] || ''}`}>
                      {ci.lifecycleState}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-gray-400 text-xs">
                    {(ci.dependsOn || []).length || '—'}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setInspecting(ci)} title="Impact"
                      className="text-gray-400 hover:text-accent mr-2">
                      <Network size={14} />
                    </button>
                    <button onClick={() => remove(ci)} title="Delete"
                      className="text-gray-300 dark:text-gray-600 hover:text-red-500">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && meta && (
        <CiModal ci={editing === 'new' ? null : editing} meta={meta} all={items}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {inspecting && (
        <ImpactPanel ci={inspecting} queues={meta?.queues} onClose={() => setInspecting(null)} />
      )}
    </div>
  );
}

function ImpactPanel({ ci, queues, onClose }: {
  ci: ConfigItem; queues?: CmdbMeta['queues']; onClose: () => void;
}) {
  const [impact, setImpact] = useState<CiImpact | null>(null);
  const [tickets, setTickets] = useState<ItsmItem[]>([]);

  useEffect(() => {
    cmdbApi.impact(ci._id).then(setImpact).catch(() => setImpact(null));
    cmdbApi.tickets(ci._id).then((t) => setTickets(t.items || [])).catch(() => setTickets([]));
  }, [ci._id]);

  return (
    <div className="fixed inset-y-0 right-0 z-50 w-full max-w-md bg-white dark:bg-gray-800 shadow-2xl border-l border-gray-200 dark:border-gray-700 flex flex-col">
      <div className="flex items-start justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{ci.name}</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">{ci.ciType} · {queueName(ci.ownerGroup, queues) || 'unowned'}</p>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        {!impact ? (
          <Loader2 size={18} className="animate-spin text-accent" />
        ) : (
          <>
            {/* Downstream first: it is the question asked during an outage. */}
            <div>
              <h3 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                <ArrowDown size={12} /> Impacted if this fails
                <span className="ml-auto text-gray-400">{impact.impactCount}</span>
              </h3>
              {impact.downstream.length === 0 ? (
                <p className="text-xs text-gray-400">Nothing depends on it.</p>
              ) : (
                <ul className="space-y-1">
                  {impact.downstream.map((d) => (
                    <li key={d._id} className="text-xs text-gray-700 dark:text-gray-200">
                      {d.name}
                      <span className="text-gray-400"> · {d.ciType}
                        {d.ownerGroup ? ` · ${queueName(d.ownerGroup, queues)}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
              {impact.affectedOwnerGroups.length > 0 && (
                <p className="mt-2 text-[11px] text-amber-600 dark:text-amber-500">
                  Tell: {impact.affectedOwnerGroups.map((o) => queueName(o, queues)).join(', ')}
                </p>
              )}
            </div>

            <div>
              <h3 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2 flex items-center gap-1.5">
                <ArrowUp size={12} /> This depends on
              </h3>
              {impact.upstream.length === 0 ? (
                <p className="text-xs text-gray-400">Nothing.</p>
              ) : (
                <ul className="space-y-1">
                  {impact.upstream.map((d) => (
                    <li key={d._id} className="text-xs text-gray-700 dark:text-gray-200">
                      {d.name}<span className="text-gray-400"> · {d.ciType}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        <div>
          <h3 className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-2">
            Operational work against it
          </h3>
          {tickets.length === 0 ? (
            <p className="text-xs text-gray-400">Nothing open or closed.</p>
          ) : (
            <ul className="space-y-1">
              {tickets.map((t) => (
                <li key={t._id} className="text-xs text-gray-700 dark:text-gray-200">
                  <span className="font-mono text-[10px] text-gray-400">{t.key}</span>{' '}
                  {t.title}<span className="text-gray-400"> · {t.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function CiModal({ ci, meta, all, onClose, onSaved }: {
  ci: ConfigItem | null;
  meta: CmdbMeta;
  all: ConfigItem[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: ci?.name || '',
    ciType: ci?.ciType || 'service',
    lifecycleState: ci?.lifecycleState || 'active',
    ownerGroup: ci?.ownerGroup || '',
    description: ci?.description || '',
    environment: ci?.environment || '',
    orgId: ci?.orgId || '',
  });
  const [dependsOn, setDependsOn] = useState<string[]>(ci?.dependsOn || []);
  const [saving, setSaving] = useState(false);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  // An asset never appears in its own dependency list: a self-edge makes the
  // impact walk report it as its own blast radius, and the server refuses it.
  const others = all.filter((c) => c._id !== ci?._id);

  const save = async () => {
    if (!form.name.trim()) { toast.error('It needs a name'); return; }
    setSaving(true);
    try {
      const payload = { ...form, dependsOn };
      const saved = ci ? await cmdbApi.update(ci._id, payload)
                       : await cmdbApi.create(payload);
      if (saved.warning) toast(saved.warning, { icon: '⚠️', duration: 7000 });
      else toast.success(ci ? 'Updated' : 'Created');
      onSaved();
    } catch (err: any) {
      toast.error(err.message || 'Could not save', { duration: 8000 });
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100';
  const label = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {ci ? 'Edit asset' : 'New asset'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          <div>
            <label className={label}>Name</label>
            <input className={field} value={form.name} autoFocus
              onChange={(e) => set('name', e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Type</label>
              <select className={field} value={form.ciType}
                onChange={(e) => set('ciType', e.target.value)}>
                {meta.ciTypes.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Lifecycle</label>
              <select className={field} value={form.lifecycleState}
                onChange={(e) => set('lifecycleState', e.target.value)}>
                {meta.lifecycleStates.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              {/* The queue is a user group, picked by id (EP-27 FE-4). A legacy free-text
                  owner stays selectable as itself until it is migrated or changed. */}
              <label className={label} htmlFor="cmdb-owner-queue">Owner queue</label>
              <select id="cmdb-owner-queue" className={field} value={form.ownerGroup}
                aria-describedby="cmdb-owner-queue-help"
                onChange={(e) => set('ownerGroup', e.target.value)}>
                <option value="">No queue</option>
                {(meta.queues || []).map((q) => <option key={q.id} value={q.id}>{q.name}</option>)}
                {form.ownerGroup && !(meta.queues || []).some((q) => q.id === form.ownerGroup) && (
                  <option value={form.ownerGroup}>{form.ownerGroup} (not a group yet)</option>
                )}
              </select>
              <p id="cmdb-owner-queue-help" className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                The user group tickets on this asset land in. Its members are told when one does.
              </p>
            </div>
            <div>
              <label className={label}>Environment</label>
              <input className={field} value={form.environment}
                onChange={(e) => set('environment', e.target.value)} />
            </div>
          </div>
          <div>
            <label className={label}>
              Client orgId <span className="text-gray-400">— for a service offering</span>
            </label>
            <input className={field} value={form.orgId}
              onChange={(e) => set('orgId', e.target.value.toUpperCase())} />
            <p className="mt-1 text-[11px] text-gray-400">
              Decides whose Operations project a ticket about this asset lands in.
            </p>
          </div>
          <div>
            <label className={label}>Depends on</label>
            <select multiple size={5} className={field} value={dependsOn}
              onChange={(e) => setDependsOn(
                Array.from(e.target.selectedOptions).map((o) => o.value))}>
              {others.map((c) => (
                <option key={c._id} value={c._id}>{c.name} · {c.ciType}</option>
              ))}
            </select>
            <p className="mt-1 text-[11px] text-gray-400">
              What this needs. Impact reverses these to work out what breaks
              when this does.
            </p>
          </div>
          <div>
            <label className={label}>Description</label>
            <textarea className={field} rows={2} value={form.description}
              onChange={(e) => set('description', e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
